import React from "react";
import { Link } from "react-router-dom";
import Chart from "../../Components/Charts/Chart/Chart";
import Loader from "../../Components/Loader/Loader";
import API from "../../API/REST_API";

// Round number to 2 decimal places to remove floating point errors
const roundNumber = (num) => {
  return Math.round(num * 100) / 100;
};

// Clean array of numbers by rounding each to 2 decimals
const cleanNumberArray = (arr) => {
  if (!Array.isArray(arr)) return arr;
  return arr.map(val => typeof val === 'number' ? roundNumber(val) : val);
};

// Calculate monthly payment for fixed expenses with payment periods
const getMonthlyPayment = (expense) => {
  if (!expense.from_date || !expense.to_date || expense.to_date === 999999) {
    // Ongoing expense or no payment period - sum is the monthly amount
    return expense.sum;
  }
  
  // Calculate number of payments from from_date to to_date
  const fromYear = Math.floor(expense.from_date / 100);
  const fromMonth = expense.from_date % 100;
  const toYear = Math.floor(expense.to_date / 100);
  const toMonth = expense.to_date % 100;
  const totalPayments = (toYear - fromYear) * 12 + (toMonth - fromMonth) + 1;
  
  // Divide total sum by number of payments to get monthly payment
  return totalPayments > 0 ? expense.sum / totalPayments : expense.sum;
};

export default class DashboardPage extends React.Component {
  state = {
    loaded: false,
    pieChart: {
      options: {
        legend: {
          show: true,
          position: "bottom",
        },
        chart: {
          id: "dashboard-pie",
        },
        tooltip: {
          y: {
            formatter: function (val) {
              const numVal = typeof val === 'number' ? val : parseFloat(val);
              if (isNaN(numVal)) return val;
              const rounded = Math.round(numVal * 100) / 100;
              return new Intl.NumberFormat("en-US", {
                maximumFractionDigits: 2,
                minimumFractionDigits: 0,
              }).format(rounded);
            },
          },
        },
        labels: [
          "Income",
          "Saving",
          "Fixed Expenses",
          "Expenses",
          "Free Budget",
        ],
      },
      series: [0, 0, 0, 0, 0],
      width: 350,
      type: "pie",
      title: "",
    },
    barChart: {
      options: {
        chart: {
          type: "bar",
        },
        tooltip: {
          y: {
            formatter: function (val) {
              const numVal = typeof val === 'number' ? val : parseFloat(val);
              if (isNaN(numVal)) return val;
              const rounded = Math.round(numVal * 100) / 100;
              return new Intl.NumberFormat("en-US", {
                maximumFractionDigits: 2,
                minimumFractionDigits: 0,
              }).format(rounded);
            },
          },
        },
        plotOptions: {
          bar: {
            columnWidth: "50%",
            distributed: true,
          },
        },
        dataLabels: {
          enabled: false,
        },
        legend: {
          show: false,
        },
        xaxis: {
          categories: [["Income"], ["Saving"], ["Fixed"], ["Expenses"], ["Free"]],
          labels: {
            style: {
              fontSize: "12px",
            },
          },
        },
        colors: ["#10b981", "#3b82f6", "#f59e0b", "#ef4444", "#6366f1"],
      },
      series: [
        {
          data: [0, 0, 0, 0, 0],
        },
      ],
      width: 400,
      type: "bar",
      title: "",
    },
    donutChart: {
      options: {
        chart: {
          id: "dashboard-donut",
        },
        tooltip: {
          y: {
            formatter: function (val) {
              const numVal = typeof val === 'number' ? val : parseFloat(val);
              if (isNaN(numVal)) return val;
              const rounded = Math.round(numVal * 100) / 100;
              return new Intl.NumberFormat("en-US", {
                maximumFractionDigits: 2,
                minimumFractionDigits: 0,
              }).format(rounded);
            },
          },
        },
        legend: {
          show: true,
          position: "bottom",
        },
        labels: [
          "Income",
          "Saving",
          "Fixed Expenses",
          "Expenses",
          "Free Budget",
        ],
        colors: ["#10b981", "#3b82f6", "#f59e0b", "#ef4444", "#6366f1"],
        plotOptions: {
          pie: {
            donut: {
              size: "65%",
            },
          },
        },
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            const numVal = typeof val === 'number' ? val : parseFloat(val);
            if (isNaN(numVal)) return val;
            const rounded = Math.round(numVal * 100) / 100;
            return Math.round(rounded) + "%";
          },
        },
      },
      series: [0, 0, 0, 0, 0],
      width: 350,
      type: "donut",
      title: "",
    },
    radialBarChart: {
      options: {
        chart: {
          type: "radialBar",
        },
        tooltip: {
          y: {
            formatter: function (val) {
              const numVal = typeof val === 'number' ? val : parseFloat(val);
              if (isNaN(numVal)) return val;
              const rounded = Math.round(numVal * 100) / 100;
              return new Intl.NumberFormat("en-US", {
                maximumFractionDigits: 2,
                minimumFractionDigits: 0,
              }).format(rounded);
            },
          },
        },
        plotOptions: {
          radialBar: {
            dataLabels: {
              name: {
                fontSize: "12px",
              },
              value: {
                fontSize: "14px",
                formatter: function (val) {
                  const numVal = typeof val === 'number' ? val : parseFloat(val);
                  if (isNaN(numVal)) return val;
                  const rounded = Math.round(numVal * 100) / 100;
                  return new Intl.NumberFormat("en-US", {
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 0,
                  }).format(rounded);
                },
              },
              total: {
                show: true,
                label: "Total",
                formatter: function (w) {
                  const total = w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                  const rounded = Math.round(total * 100) / 100;
                  return new Intl.NumberFormat("en-US", {
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 0,
                  }).format(rounded);
                },
              },
            },
          },
        },
        labels: ["Income", "Saving", "Fixed", "Expenses", "Free"],
        colors: ["#10b981", "#3b82f6", "#f59e0b", "#ef4444", "#6366f1"],
      },
      series: [0, 0, 0, 0, 0],
      width: 350,
      type: "radialBar",
      title: "",
    },
    lineChart: {
      options: {
        chart: {
          type: "line",
        },
        tooltip: {
          y: {
            formatter: function (val) {
              const numVal = typeof val === 'number' ? val : parseFloat(val);
              if (isNaN(numVal)) return val;
              const rounded = Math.round(numVal * 100) / 100;
              return new Intl.NumberFormat("en-US", {
                maximumFractionDigits: 2,
                minimumFractionDigits: 0,
              }).format(rounded);
            },
          },
        },
        stroke: {
          curve: "smooth",
          width: 3,
        },
        markers: {
          size: 5,
        },
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            const numVal = typeof val === 'number' ? val : parseFloat(val);
            if (isNaN(numVal)) return val;
            const rounded = Math.round(numVal * 100) / 100;
            return new Intl.NumberFormat("en-US", {
              maximumFractionDigits: 2,
              minimumFractionDigits: 0,
            }).format(rounded);
          },
        },
        xaxis: {
          categories: ["Income", "Saving", "Fixed", "Expenses", "Free"],
          labels: {
            style: {
              fontSize: "12px",
            },
          },
        },
        colors: ["#10b981", "#3b82f6", "#f59e0b", "#ef4444", "#6366f1"],
        legend: {
          show: true,
          position: "bottom",
        },
      },
      series: [
        {
          name: "Budget",
          data: [0, 0, 0, 0, 0],
        },
      ],
      width: 400,
      type: "line",
      title: "",
    },
    totals: {
      income: 0,
      saving: 0,
      fixedExpenses: 0,
      expenses: 0,
      freeBudget: 0,
    },
  };

  componentDidMount() {
    this.loadCurrentMonthData();
    this.handleResize();
    window.addEventListener("resize", this.handleResize);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize);
  }

  handleResize = () => {
    let pieChart = JSON.parse(JSON.stringify(this.state.pieChart));
    let barChart = JSON.parse(JSON.stringify(this.state.barChart));
    let donutChart = JSON.parse(JSON.stringify(this.state.donutChart));
    let radialBarChart = JSON.parse(JSON.stringify(this.state.radialBarChart));
    let lineChart = JSON.parse(JSON.stringify(this.state.lineChart));
    
    if (window.innerWidth < 850) {
      pieChart.width = 300;
      barChart.width = 300;
      donutChart.width = 300;
      radialBarChart.width = 300;
      lineChart.width = 300;
    } else {
      pieChart.width = 350;
      barChart.width = 400;
      donutChart.width = 350;
      radialBarChart.width = 350;
      lineChart.width = 400;
    }
    this.setState({ 
      pieChart, 
      barChart, 
      donutChart, 
      radialBarChart, 
      lineChart
    });
  };

  loadCurrentMonthData = async () => {
    const date = new Date();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const yyyymm = `${year}${month}`;

    const url =
      this.props.baseUrl +
      this.props.apiRouts.FETCH_BY_DATE +
      yyyymm +
      "&to_date=" +
      yyyymm;

    try {
      const expenses = await API.Get(url);

      if (expenses) {
        let totals = {
          income: 0,
          saving: 0,
          fixedExpenses: 0,
          expenses: 0,
          freeBudget: 0,
        };

        for (let exp of expenses) {
          if (exp.type === "income") {
            totals.income += exp.sum;
          } else if (exp.type === "saving") {
            totals.saving += exp.sum;
          } else if (exp.type === "expense") {
            totals.expenses += exp.sum;
          }
        }

        // Fetch fixed expenses separately
        const constExpUrl = url + "&type=const_expense";
        const constExpenses = await API.Get(constExpUrl);

        if (constExpenses) {
          for (let exp of constExpenses) {
            // Use monthly payment calculation for expenses with payment periods
            totals.fixedExpenses += getMonthlyPayment(exp);
          }
        }

        totals.freeBudget =
          totals.income - totals.saving - totals.fixedExpenses - totals.expenses;

        // Clean series data to remove floating point errors
        const series = cleanNumberArray([
          totals.income,
          totals.saving,
          totals.fixedExpenses,
          totals.expenses,
          totals.freeBudget,
        ]);

        // Calculate total for percentage calculations
        const total = series.reduce((a, b) => a + Math.abs(b), 0);
        
        // Update all charts
        let pieChart = JSON.parse(JSON.stringify(this.state.pieChart));
        let barChart = JSON.parse(JSON.stringify(this.state.barChart));
        let donutChart = JSON.parse(JSON.stringify(this.state.donutChart));
        let radialBarChart = JSON.parse(JSON.stringify(this.state.radialBarChart));
        let lineChart = JSON.parse(JSON.stringify(this.state.lineChart));
        
        // Pie chart - already cleaned
        pieChart.series = series;
        
        // Bar chart - already cleaned
        barChart.series[0].data = series;
        
        // Donut chart - already cleaned
        donutChart.series = series;
        
        // Radial bar chart - convert to percentages (already rounded by Math.round)
        radialBarChart.series = total > 0 
          ? series.map(val => Math.round((Math.abs(val) / total) * 100))
          : [0, 0, 0, 0, 0];
        
        // Line chart - already cleaned
        lineChart.series[0].data = series;
        
        // Update colors for Free Budget based on positive/negative
        const freeBudgetColor = totals.freeBudget >= 0 ? "#6366f1" : "#ef4444";
        const colors = ["#10b981", "#3b82f6", "#f59e0b", "#ef4444", freeBudgetColor];
        
        pieChart.options.colors = colors;
        barChart.options.colors = colors;
        donutChart.options.colors = colors;
        radialBarChart.options.colors = colors;
        lineChart.options.colors = colors;

        this.setState({
          pieChart,
          barChart,
          donutChart,
          radialBarChart,
          lineChart,
          totals,
          loaded: true,
        });
      }
    } catch (error) {
      console.error("Error loading dashboard data:", error);
      this.setState({ loaded: true });
    }
  };

  formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "decimal",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  render() {
    const { user } = this.props;
    const { totals, loaded } = this.state;
    const greeting = user?.first_name
      ? `Welcome back, ${user.first_name}`
      : "Welcome back";

    const currentMonth = new Date().toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });

    return (
      <div className="h-full overflow-y-auto bg-slate-50 p-3 sm:p-4 md:p-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-4 sm:mb-6">
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">{greeting}</h1>
            <p className="mt-0.5 sm:mt-1 text-xs sm:text-sm text-gray-500">
              Here's your financial overview for {currentMonth}
            </p>
          </div>

          {/* Summary Cards */}
          <div className="mb-3 sm:mb-4 grid grid-cols-2 gap-1.5 sm:gap-2 md:grid-cols-3 lg:grid-cols-5">
            <Link
              to="/budget/income"
              className="group rounded-lg border border-slate-200 bg-white p-2 shadow-sm transition hover:shadow-md"
            >
              <p className="text-[10px] sm:text-xs font-medium text-gray-500 truncate">Income</p>
              <p className="mt-0.5 text-sm sm:text-base md:text-lg font-semibold text-green-600 truncate">
                {loaded ? this.formatCurrency(totals.income) : "..."}
              </p>
            </Link>
            <Link
              to="/budget/saving"
              className="group rounded-lg border border-slate-200 bg-white p-2 shadow-sm transition hover:shadow-md"
            >
              <p className="text-[10px] sm:text-xs font-medium text-gray-500 truncate">Savings</p>
              <p className="mt-0.5 text-sm sm:text-base md:text-lg font-semibold text-blue-600 truncate">
                {loaded ? this.formatCurrency(totals.saving) : "..."}
              </p>
            </Link>
            <Link
              to="/budget/fixedexpenses"
              className="group rounded-lg border border-slate-200 bg-white p-2 shadow-sm transition hover:shadow-md"
            >
              <p className="text-[10px] sm:text-xs font-medium text-gray-500 truncate">Fixed</p>
              <p className="mt-0.5 text-sm sm:text-base md:text-lg font-semibold text-amber-600 truncate">
                {loaded ? this.formatCurrency(totals.fixedExpenses) : "..."}
              </p>
            </Link>
            <Link
              to="/budget/expenses"
              className="group rounded-lg border border-slate-200 bg-white p-2 shadow-sm transition hover:shadow-md"
            >
              <p className="text-[10px] sm:text-xs font-medium text-gray-500 truncate">Expenses</p>
              <p className="mt-0.5 text-sm sm:text-base md:text-lg font-semibold text-red-600 truncate">
                {loaded ? this.formatCurrency(totals.expenses) : "..."}
              </p>
            </Link>
            <div className="rounded-lg border border-slate-200 bg-white p-2 shadow-sm col-span-2 md:col-span-1">
              <p className="text-[10px] sm:text-xs font-medium text-gray-500 truncate">Free Budget</p>
              <p
                className={`mt-0.5 text-sm sm:text-base md:text-lg font-semibold truncate ${
                  totals.freeBudget >= 0 ? "text-indigo-600" : "text-red-600"
                }`}
              >
                {loaded ? this.formatCurrency(totals.freeBudget) : "..."}
              </p>
            </div>
          </div>

          {/* Charts Section - Flexbox with wrap */}
          <div className="mb-4 sm:mb-6 flex flex-wrap gap-3 sm:gap-4">
            {/* Pie Chart */}
            <div style={{ flex: '1 1 300px', minWidth: '280px', maxWidth: '400px' }} className="rounded-lg border border-slate-200 bg-white p-3 sm:p-4 md:p-6 shadow-sm overflow-hidden">
              <h2 className="mb-2 sm:mb-3 text-base sm:text-lg font-semibold text-gray-900">
                Budget Distribution (Pie)
              </h2>
              {!loaded ? (
                <div className="flex h-48 sm:h-56 md:h-64 items-center justify-center">
                  <Loader />
                </div>
              ) : (
                <div className="flex justify-center overflow-hidden">
                  <Chart data={this.state.pieChart} />
                </div>
              )}
            </div>
            
            {/* Bar Chart */}
            <div style={{ flex: '1 1 300px', minWidth: '280px', maxWidth: '400px' }} className="rounded-lg border border-slate-200 bg-white p-3 sm:p-4 md:p-6 shadow-sm overflow-hidden">
              <h2 className="mb-2 sm:mb-3 text-base sm:text-lg font-semibold text-gray-900">
                Budget Comparison (Bar)
              </h2>
              {!loaded ? (
                <div className="flex h-48 sm:h-56 md:h-64 items-center justify-center">
                  <Loader />
                </div>
              ) : (
                <div className="flex justify-center overflow-hidden">
                  <Chart data={this.state.barChart} />
                </div>
              )}
            </div>
            
            {/* Donut Chart */}
            <div style={{ flex: '1 1 300px', minWidth: '280px', maxWidth: '400px' }} className="rounded-lg border border-slate-200 bg-white p-3 sm:p-4 md:p-6 shadow-sm overflow-hidden">
              <h2 className="mb-2 sm:mb-3 text-base sm:text-lg font-semibold text-gray-900">
                Budget Distribution (Donut)
              </h2>
              {!loaded ? (
                <div className="flex h-48 sm:h-56 md:h-64 items-center justify-center">
                  <Loader />
                </div>
              ) : (
                <div className="flex justify-center overflow-hidden">
                  <Chart data={this.state.donutChart} />
                </div>
              )}
            </div>
            
            {/* Radial Bar Chart */}
            <div style={{ flex: '1 1 300px', minWidth: '280px', maxWidth: '400px' }} className="rounded-lg border border-slate-200 bg-white p-3 sm:p-4 md:p-6 shadow-sm overflow-hidden">
              <h2 className="mb-2 sm:mb-3 text-base sm:text-lg font-semibold text-gray-900">
                Budget Overview (Radial)
              </h2>
              {!loaded ? (
                <div className="flex h-48 sm:h-56 md:h-64 items-center justify-center">
                  <Loader />
                </div>
              ) : (
                <div className="flex justify-center overflow-hidden">
                  <Chart data={this.state.radialBarChart} />
                </div>
              )}
            </div>
            
            {/* Line Chart */}
            <div style={{ flex: '1 1 300px', minWidth: '280px', maxWidth: '400px' }} className="rounded-lg border border-slate-200 bg-white p-3 sm:p-4 md:p-6 shadow-sm overflow-hidden">
              <h2 className="mb-2 sm:mb-3 text-base sm:text-lg font-semibold text-gray-900">
                Budget Trend (Line)
              </h2>
              {!loaded ? (
                <div className="flex h-48 sm:h-56 md:h-64 items-center justify-center">
                  <Loader />
                </div>
              ) : (
                <div className="flex justify-center overflow-hidden">
                  <Chart data={this.state.lineChart} />
                </div>
              )}
            </div>
            
          </div>

          {/* Quick Actions and Info */}
          <div className="grid gap-3 sm:gap-4 lg:grid-cols-2 pb-4 sm:pb-6">
            <div className="rounded-lg border border-slate-200 bg-white p-3 sm:p-4 md:p-6 shadow-sm">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900">
                Quick Actions
              </h2>
              <div className="mt-2 sm:mt-3 space-y-2 sm:space-y-2.5">
                <Link
                  to="/budget/expenses"
                  className="flex items-center gap-2 sm:gap-3 rounded-lg border border-slate-200 p-2 sm:p-2.5 text-xs sm:text-sm font-medium text-gray-700 transition hover:bg-slate-50"
                >
                  <span className="flex h-7 w-7 sm:h-8 sm:w-8 items-center justify-center rounded-full bg-indigo-100 text-indigo-600">
                    <svg
                      className="h-3.5 w-3.5 sm:h-4 sm:w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 4.5v15m7.5-7.5h-15"
                      />
                    </svg>
                  </span>
                  Add new expense
                </Link>
                <Link
                  to="/reports"
                  className="flex items-center gap-2 sm:gap-3 rounded-lg border border-slate-200 p-2 sm:p-2.5 text-xs sm:text-sm font-medium text-gray-700 transition hover:bg-slate-50"
                >
                  <span className="flex h-7 w-7 sm:h-8 sm:w-8 items-center justify-center rounded-full bg-indigo-100 text-indigo-600">
                    <svg
                      className="h-3.5 w-3.5 sm:h-4 sm:w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z"
                      />
                    </svg>
                  </span>
                  View reports
                </Link>
                <Link
                  to="/settings"
                  className="flex items-center gap-2 sm:gap-3 rounded-lg border border-slate-200 p-2 sm:p-2.5 text-xs sm:text-sm font-medium text-gray-700 transition hover:bg-slate-50"
                >
                  <span className="flex h-7 w-7 sm:h-8 sm:w-8 items-center justify-center rounded-full bg-indigo-100 text-indigo-600">
                    <svg
                      className="h-3.5 w-3.5 sm:h-4 sm:w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                      />
                    </svg>
                  </span>
                  Manage settings
                </Link>
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-3 sm:p-4 md:p-6 shadow-sm">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900">
                Kakeibo Method
              </h2>
              <p className="mt-1 sm:mt-2 text-xs sm:text-sm text-gray-500">
                The Japanese art of mindful spending
              </p>
              <div className="mt-2 sm:mt-3 space-y-2 sm:space-y-2.5 text-xs sm:text-sm text-gray-600">
                <div className="flex items-start gap-2 sm:gap-3">
                  <span className="flex h-5 w-5 sm:h-6 sm:w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600">
                    1
                  </span>
                  <span>Record your income at the start of the month</span>
                </div>
                <div className="flex items-start gap-2 sm:gap-3">
                  <span className="flex h-5 w-5 sm:h-6 sm:w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600">
                    2
                  </span>
                  <span>Set aside savings and fixed expenses first</span>
                </div>
                <div className="flex items-start gap-2 sm:gap-3">
                  <span className="flex h-5 w-5 sm:h-6 sm:w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600">
                    3
                  </span>
                  <span>Track daily expenses mindfully</span>
                </div>
                <div className="flex items-start gap-2 sm:gap-3">
                  <span className="flex h-5 w-5 sm:h-6 sm:w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600">
                    4
                  </span>
                  <span>Review and reflect at month end</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
